import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Cpu, Rocket, Zap, Activity, Globe, Database, Shield, Hexagon, Link, Landmark, Building2 } from 'lucide-react';
import { Strategy } from '../types';
const iconMap: Record<string, React.ElementType> = {
  Cpu,
  Rocket,
  Zap,
  Activity,
  Globe,
  Database,
  Shield,
  Hexagon,
  Link,
  Landmark,
  Building2
};
interface StrategyCardProps {
  strategy: Strategy;
  index: number;
}
export function StrategyCard({
  strategy,
  index
}: StrategyCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = iconMap[strategy.iconName] || Database;
  const formattedIndex = (index + 1).toString().padStart(2, '0');
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.05
  }} className="bg-slate-900/40 border border-slate-800 overflow-hidden group hover:border-slate-700 transition-colors relative" data-id="element-242">
      {/* Card Header (Always Visible) */}
      <div className="p-6 md:p-8 cursor-pointer relative" onClick={() => setIsExpanded(!isExpanded)} data-id="element-243">
        {/* Accent line */}
        <div className={`absolute left-0 top-0 bottom-0 w-1 bg-gold-400 transition-transform duration-300 ${isExpanded ? 'scale-y-100' : 'scale-y-0 group-hover:scale-y-50'}`} data-id="element-244"></div>

        {/* Large Strategy Index Number */}
        <div className="absolute top-4 right-6 text-4xl font-bold text-slate-800/20 font-mono select-none pointer-events-none" data-id="element-245">
          {formattedIndex}
        </div>

        <div className="flex justify-between items-start gap-4 relative z-10" data-id="element-246">
          <div className="flex items-start gap-4" data-id="element-247">
            <div className="p-3 bg-base-950 border border-slate-800 text-gold-400" data-id="element-248">
              <Icon className="w-6 h-6" data-id="element-249" />
            </div>
            <div className="pr-16" data-id="element-250">
              <div className="text-[11px] tracking-[0.15em] text-slate-500 uppercase mb-1" data-id="element-251">
                Cluster {formattedIndex}
              </div>
              <h3 className="text-xl md:text-2xl font-semibold text-white mb-3" data-id="element-252">
                {strategy.title}
              </h3>

              <div className="flex flex-wrap gap-2 mb-4" data-id="element-253">
                <span className="px-2.5 py-1 bg-slate-800/60 text-slate-400 text-[11px] tracking-wide rounded-sm" data-id="element-254">
                  {strategy.rebalancing}
                </span>
                <span className="px-2.5 py-1 bg-gold-400/8 text-gold-400/80 text-[11px] tracking-wide rounded-sm" data-id="element-255">
                  {strategy.strategyType}
                </span>
              </div>
              <p className="text-sm text-slate-400 font-mono mb-2" data-id="element-256">
                <span className="text-slate-500" data-id="element-257">TICKERS:</span>{' '}
                {strategy.tickers}
              </p>
            </div>
          </div>
          <button className="text-slate-600 hover:text-gold-400 transition-colors p-1.5" data-id="element-258">
            {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-259" /> : <ChevronDown className="w-5 h-5" data-id="element-260" />}
          </button>
        </div>

        <p className="text-slate-300 mt-4 leading-relaxed relative z-10" data-id="element-261">
          {strategy.objective}
        </p>
      </div>

      {/* Expanded Content */}
      <AnimatePresence data-id="element-262">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden border-t border-slate-800 bg-base-950/50" data-id="element-263">
            <div className="p-6 md:p-8" data-id="element-264">
              <h4 className="text-xs tracking-[0.2em] text-gold-400/70 uppercase mb-6 flex items-center gap-3" data-id="element-265">
                <div className="w-6 h-[1px] bg-gold-400/40" data-id="element-266"></div>
                Rules & Formulas
              </h4>

              <div className="grid gap-6" data-id="element-267">
                {strategy.rules.map((rule, idx) => <div key={idx} className="relative pl-4 border-l border-slate-800" data-id="element-268">
                    <div className="absolute left-[-5px] top-1.5 w-2 h-2 bg-slate-800 rotate-45" data-id="element-269"></div>
                    <h5 className="text-white font-bold mb-2" data-id="element-270">{rule.title}</h5>

                    {rule.formula && <div className="mb-3 p-3 bg-slate-900 border border-slate-800 font-mono text-gold-300 text-sm overflow-x-auto" data-id="element-271">
                        {rule.formula}
                      </div>}
                    <p className="text-slate-400 text-sm leading-relaxed" data-id="element-272">
                      {rule.description}
                    </p>
                  </div>)}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}